/* Confirmations, as a promise.

   The same module-level store as `toast`, and for the same reason: these are
   raised from event handlers scattered across the app, and a context would mean
   threading a hook through every one of them.

   What this replaces is `window.confirm` and `window.prompt`. Those block the
   whole tab, cannot be styled or themed, and — the reason it matters here — a
   browser is free to suppress them, which would turn "delete this instance"
   into a click that silently does nothing. <Dialogs/> renders the queue. */

/* When set, the dialog asks for a line of text instead of a yes/no, and
   resolves to what was typed (or null if it was dismissed). Naming a preset is
   the only thing that needs this, and it needs it for the same reason the
   confirmations are here: `window.prompt` blocks the tab, cannot be themed, and
   a browser is free to suppress it entirely. */
export interface DialogPrompt {
  label: string;
  placeholder?: string;
  initial?: string;
  maxLength?: number;
}

export interface DialogRequest {
  id: number;
  title: string;
  body: string;
  confirmLabel: string;
  danger: boolean;
  /* When set, the confirm button stays disabled until this exact string is
     typed. For the irreversible ones: deleting a tenant takes its audio with
     it, and a misplaced click should not be enough. */
  typeToConfirm?: string;
  prompt?: DialogPrompt;
  resolve: (answer: boolean | string | null) => void;
}

export interface DialogOptions {
  title: string;
  body: string;
  confirmLabel?: string;
  danger?: boolean;
  typeToConfirm?: string;
}

export interface PromptOptions extends DialogOptions {
  prompt: DialogPrompt;
}

let queue: DialogRequest[] = [];
let seq = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function ask(
  options: DialogOptions & { prompt?: DialogPrompt },
  resolve: (answer: boolean | string | null) => void,
): void {
  queue = [
    ...queue,
    {
      id: ++seq,
      title: options.title,
      body: options.body,
      confirmLabel: options.confirmLabel ?? "Confirm",
      danger: options.danger ?? false,
      typeToConfirm: options.typeToConfirm,
      prompt: options.prompt,
      resolve,
    },
  ];
  emit();
}

/** Ask, and resolve to what they chose. Never rejects. */
export function confirmDialog(options: DialogOptions): Promise<boolean> {
  return new Promise((resolve) => {
    ask(options, (answer) => resolve(answer === true));
  });
}

/** Ask for a line of text. Resolves to null if it was dismissed. */
export function promptDialog(options: PromptOptions): Promise<string | null> {
  return new Promise((resolve) => {
    ask(options, (answer) => resolve(typeof answer === "string" ? answer : null));
  });
}

/** Answer the open dialog and drop it. */
export function settleDialog(id: number, answer: boolean | string | null): void {
  const found = queue.find((request) => request.id === id);
  if (!found) return;
  queue = queue.filter((request) => request.id !== id);
  emit();
  found.resolve(answer);
}

export function subscribeDialogs(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function dialogSnapshot(): DialogRequest[] {
  return queue;
}
